import { useQuery } from '@apollo/client';
import { MoreVertRounded } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import useVerticalViewStyles from 'src/CollectionsPage/CollectionItems/VerticalViewStyles';
import SharedPageModal from 'src/components/Modals/SharedPageModal';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import SimpleItemTab from 'src/components/Tabs/SimpleItemTab/SimpleItemTab';
import type { ActionItem } from 'src/constants';
import { FD_MIMETYPE_SLUG, FD_NAME_SLUG, PROXY_FORMAT_SLUG, SHARED_MEDIA_PATH } from 'src/constants';
import { buildFormatsQ, buildSharedFormatsQ } from 'src/dynamoQueries/queryHelpers';
import MediaRouterComponent from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaRouterComponent';
import type { VideoJsOptions } from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/types';
import { getFirstNestedWithSaneDefault, getMetadataFieldValue, getMetadataValue } from 'src/helpers/common';
import { useMetaQ } from 'src/hooks/meta';

// Asset type compatible with SimpleItemTab
interface Asset {
  id: string;
  [key: string]: unknown;
}

interface CollectionVerticalViewProps {
  shared: boolean;
  asset: Asset;
  downloadable: boolean;
  handleDownload: () => void;
  onVideoEnded: () => void;
  autoplay: boolean;
}

interface FormatData {
  id?: string;
}

interface FormatsQueryResult {
  formats?: FormatData[];
  sharedFormats?: FormatData[];
  [key: string]: FormatData[] | undefined;
}

// VideoJsProps structure expected by MediaRouterComponent
interface VideoJsComponentProps {
  options?: VideoJsOptions;
  onEnded?: () => void;
  autoplay?: boolean;
}

const DOWNLOAD_ACTION: ActionItem[] = [{ label: 'Download', icon: <DownloadIcon style={{ color: 'currentColor' }} /> }];

const CollectionVerticalView: React.FC<CollectionVerticalViewProps> = (props) => {
  const classes = useVerticalViewStyles();
  const { shared, asset, downloadable, handleDownload, onVideoEnded, autoplay } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAction = (): void => {
    handleDownload();
    handleClose();
  };

  const {
    data: assetMeta,
    loading: loadingAssetMeta,
    error: assetMetaError,
  } = useMetaQ(
    {
      skip: !asset?.id,
      variables: { globalId: asset?.id, definitionSlugIn: [FD_NAME_SLUG] },
    },
    shared,
  );

  const {
    data: proxyFormatData,
    loading: loadingProxy,
    error: errorProxy,
  } = useQuery<FormatsQueryResult>(shared ? buildSharedFormatsQ() : buildFormatsQ(), {
    skip: !asset?.id,
    variables: { asset_Id: asset?.id, fmtType_Slug: PROXY_FORMAT_SLUG },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const proxyFormat = getFirstNestedWithSaneDefault<FormatData[]>(
    proxyFormatData as Record<string, FormatData[]> | undefined,
    [{}],
  )[0];
  const sharedPreviewUrl = `${SHARED_MEDIA_PATH}/` + `${proxyFormat?.id}`;

  const {
    data: formatData,
    loading: loadingFormatData,
    error: formatDataError,
  } = useMetaQ(
    {
      skip: !proxyFormat?.id,
      variables: { globalId: proxyFormat.id, definitionSlugIn: [FD_MIMETYPE_SLUG] },
    },
    shared,
  );

  const mimetype = getMetadataValue(formatData as Record<string, unknown> | undefined, FD_MIMETYPE_SLUG);

  if (loadingAssetMeta || loadingFormatData || loadingProxy || !asset.id) return <CircularProgress color="secondary" />;
  if (assetMetaError || formatDataError || errorProxy) return <div>Error!</div>;

  const name = getMetadataFieldValue(assetMeta, FD_NAME_SLUG);
  const videoJsProps: VideoJsComponentProps = {
    options: { autoplay },
    onEnded: onVideoEnded,
  };

  return (
    <div>
      <div className={classes.itemTitle}>
        {showPreview && (
          <SharedPageModal
            sharedPreviewUrl={sharedPreviewUrl}
            showPreview={showPreview}
            mimetype={mimetype}
            onClose={() => setShowPreview(false)}
          />
        )}
        <Typography variant="h2">{name}</Typography>
        {downloadable && (
          <div>
            <IconButton
              id="cyp-collection-action-menu"
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={handleClick}
              size="large"
            >
              <MoreVertRounded />
            </IconButton>
            <StyledContextMenu
              actions={DOWNLOAD_ACTION}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onAction={handleAction}
              testIdPrefix="cyp-collection-action"
              id="long-menu"
            />
          </div>
        )}
      </div>
      <MediaRouterComponent
        itemId={asset.id}
        onImageClick={() => setShowPreview(true)}
        videoJsProps={videoJsProps}
        shared={shared}
      />
      <SimpleItemTab asset={asset} shared={shared} />
    </div>
  );
};

export type { Asset, CollectionVerticalViewProps };
export default CollectionVerticalView;
